import React, {useEffect, useState} from 'react';
import {
  FormControl,
  Button,
  MenuItem,
  Box,
  Typography,
  Divider,
  Stack,
  Container
} from '@mui/material';
import * as Yup from 'yup';
import {useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import FormProvider, {
  RHFCheckbox,
  RHFTextField,
  RHFSelect
} from 'src/components/hook-form';

import {useSnackbar} from 'src/components/snackbar';
import Drawer, {DrawerProps} from '@mui/material/Drawer';
import Scrollbar from '@components/scrollbar';
import StarquestionerFeedback from './StarquestionerFeedback';
import RatingScaleQuestionType from './RatingScaleQuestionType';
import NPSRatingscale from './NPSRatingscale';
import Datefromto from './Datefromto';
import SingleQuestionTypeFeedback from './SinglequestionFeedback';
import MultipleQuestionTypeFeedback from './Multiplefeedback';

interface Choice {
  id: number;
  value: string | undefined;
}
// interface CustomEditProps {
//   [index: number]: {
//     icon: React.ReactElement;
//     label: string;
//     id: number;
//   };
// }
interface handleEditQuestionprops {
  id: number;
  question: string;
  questionType: string;
  disableComments: boolean;
  makecommentmandatory: boolean;
  markasMandatory: boolean;
  averagecourseRating?: boolean;
  choices?: Choice[];
  daterange?: {
    dateformat: string;
    startDate: Date | undefined;
    stopDate: Date | undefined;
  };
  npsstring?: {
    notlikely: string;
    likely: string;
  };
  starRating?: number | undefined;
  ratingScale?: object;
}
type QuestionFormProps = DrawerProps & {
  questionIds: number;
  onClose: () => void;
  onSave: (formData: any) => void;
  handleEditQuestion?: handleEditQuestionprops;
};

const QuestionTypeused = [
  {value: 'single', label: 'Single'},
  {value: 'multiple', label: 'Multiple'},
  {value: 'descriptive', label: 'Comment'},
  {value: 'date', label: 'Date'},
  {value: 'nps', label: 'NPS'},
  {value: 'star', label: 'Star'},
  {value: 'ratingscale', label: 'Rating Scale'}
];
const QuestionDialogFeedback: React.FC<QuestionFormProps> = ({
  open,
  onClose,
  onSave,
  handleEditQuestion,
  questionIds,
  ...other
}) => {
  const [questionId, setQuestionId] = useState<number>(2);
  const updatedschema = Yup.object().shape({
    question: Yup.string().required('Question is required'),
    questionType: Yup.string(),
    disableComments: Yup.boolean(),
    makecommentmandatory: Yup.boolean(),
    markasMandatory: Yup.boolean(),
    averagecourseRating: Yup.boolean(),
    choices: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number(),
          value: Yup.string()
        })
      )
      .when('questionType', {
        is: (value: string) => value === 'multiple' || value === 'single',
        then: schema => {
          return Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.number().required(),
                value: Yup.string().required('Write the Option')
              })
            )
            .min(2, 'At least two choices are required');
        },
        otherwise: schema => schema.notRequired().nullable()
      }),
    daterange: Yup.object({
      dateformat: Yup.string(),
      startDate: Yup.date(),
      stopDate: Yup.date()
    }).when('questionType', {
      is: (value: string) => value === 'date',
      then: schema => {
        return Yup.object()
          .shape({
            dateformat: Yup.string().required('Date format is required'),
            startDate: Yup.date().required('Date range is required'),
            stopDate: Yup.date().required('Date range is required')
          })
          .required('Date range is required');
      },
      otherwise: schema => schema.notRequired()
    }),
    npsstring: Yup.object()
      .shape({
        notlikely: Yup.string(),
        likely: Yup.string()
      })
      .when('questionType', {
        is: (value: string) => value === 'nps',
        then: schema => {
          return Yup.object()
            .shape({
              notlikely: Yup.string().required('write some context'),
              likely: Yup.string().required('write some context')
            })
            .required('npsstring is required');
        },
        otherwise: (schema: any) => schema.optional()
      }),
    starRating: Yup.number().when('questionType', {
      is: (value: string) => value === 'star',
      then: schema => schema.required('Star rating is required'),
      otherwise: schema => schema
    }),
    ratingScale: Yup.object().when('questionType', {
      is: (value: string) => value === 'ratingscale',
      then: schema => schema.required('Rating scale is required'),
      otherwise: schema => schema
    })
  });
  const defaultValues = useMemo(
    () => ({
      question: handleEditQuestion?.question || '',
      questionType: handleEditQuestion?.questionType || 'single',
      disableComments: handleEditQuestion?.disableComments || false,
      makecommentmandatory: handleEditQuestion?.makecommentmandatory || false,
      markasMandatory: handleEditQuestion?.markasMandatory || false,
      averagecourseRating: handleEditQuestion?.averagecourseRating || false,
      choices: handleEditQuestion?.choices || [
        {id: 1, value: undefined},
        {id: 2, value: undefined}
      ],
      daterange: handleEditQuestion?.daterange || {
        dateformat: 'dd-mm-yyyy',
        startDate: undefined,
        stopDate: undefined
      },
      npsstring: handleEditQuestion?.npsstring || {
        notlikely: 'Not likely is required',
        likely: 'Likely is required'
      },
      starRating: handleEditQuestion?.starRating || 5,
      ratingScale: handleEditQuestion?.ratingScale || {}
    }),
    [handleEditQuestion]
  );
  const methods = useForm({
    resolver: yupResolver(updatedschema),
    defaultValues
  });
  const {
    control,
    reset,
    watch,
    handleSubmit,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: {isSubmitting}
  } = methods;
  const questionTypeselection = watch('questionType');
  useEffect(() => {
    if (handleEditQuestion) {
      reset(defaultValues);
    }
  }, [handleEditQuestion, defaultValues, reset]);
  useEffect(() => {
    if (questionIds) {
      setQuestionId(questionIds);
    }
  }, [questionIds]);

  const {enqueueSnackbar} = useSnackbar();
  const onSubmit = handleSubmit(async data => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      reset();
      enqueueSnackbar(
        handleEditQuestion ? 'Update success!' : 'Create success!'
      );
      let stored;
      switch (questionTypeselection) {
        case 'single':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            disableComments: data.disableComments,
            makecommentmandatory: data.makecommentmandatory,
            markasMandatory: data.markasMandatory,
            choices: data.choices
          };
          break;
        case 'multiple':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            disableComments: data.disableComments,
            makecommentmandatory: data.makecommentmandatory,
            markasMandatory: data.markasMandatory,
            choices: data.choices
          };
          break;
        case 'descriptive':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            markasMandatory: data.markasMandatory
          };
          break;
        case 'date':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            disableComments: data.disableComments,
            makecommentmandatory: data.makecommentmandatory,
            markasMandatory: data.markasMandatory,
            daterange: data.daterange
          };
          break;
        case 'nps':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            disableComments: data.disableComments,
            makecommentmandatory: data.makecommentmandatory,
            markasMandatory: data.markasMandatory,
            npsstring: data.npsstring
          };
          break;
        case 'star':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            disableComments: data.disableComments,
            makecommentmandatory: data.makecommentmandatory,
            markasMandatory: data.markasMandatory,
            starRating: data.starRating,
            averagecourseRating: data.averagecourseRating
          };
          break;
        case 'ratingscale':
          stored = {
            id: handleEditQuestion?.id || questionId,
            question: data.question,
            questionType: data.questionType,
            disableComments: data.disableComments,
            makecommentmandatory: data.makecommentmandatory,
            markasMandatory: data.markasMandatory,
            ratingScale: data.ratingScale
          };
          break;
      }
      onSave(stored);
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: {invisible: true}
        }}
        PaperProps={{
          sx: {width: '80vh'}
        }}
        {...other}>
        <Scrollbar sx={{height: 1}}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{p: 2.5}}>
            <Typography variant="h4"> Add Feedback Question </Typography>
          </Stack>

          <Stack
            spacing={2.5}
            justifyContent="center"
            sx={{
              p: 0,
              bgcolor: 'background.neutral'
            }}>
            <Divider sx={{borderStyle: 'dashed'}} />
          </Stack>

          <Container maxWidth="md">
            <FormControl fullWidth margin="normal">
              <Controller
                name="questionType"
                control={control}
                defaultValue=""
                render={({field}) => (
                  <RHFSelect
                    name="questionType"
                    label="Question Type"
                    value={questionTypeselection}>
                    {QuestionTypeused.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                )}
              />
            </FormControl>

            <FormControl component="fieldset" margin="normal" fullWidth>
              <Controller
                name="question"
                control={control}
                render={({field}) => {
                  return (
                    <RHFTextField
                      name="question"
                      label="Question"
                      sx={{marginY: '1vh'}}
                    />
                  );
                }}
              />
            </FormControl>
            <FormControl component="fieldset" margin="normal" fullWidth>
              {(questionTypeselection === 'single' ||
                questionTypeselection === 'multiple') && (
                <Controller
                  name="choices"
                  control={control}
                  render={({field}) =>
                    questionTypeselection === 'single' ? (
                      <SingleQuestionTypeFeedback
                        field={field}
                        name="choices"
                      />
                    ) : (
                      <MultipleQuestionTypeFeedback
                        field={field}
                        name="choices"
                      />
                    )
                  }
                />
              )}
              {questionTypeselection === 'date' && (
                <Controller
                  name="daterange"
                  control={control}
                  render={({field}) => {
                    return <Datefromto field={field} name="daterange" />;
                  }}
                />
              )}
              {questionTypeselection === 'nps' && (
                <Controller
                  name="npsstring"
                  control={control}
                  render={({field}) => {
                    return <NPSRatingscale name="npsstring" field={field} />;
                  }}
                />
              )}
              {questionTypeselection === 'star' && (
                <Controller
                  name="starRating"
                  control={control}
                  render={({field}) => {
                    return <StarquestionerFeedback field={field} />;
                  }}
                />
              )}
              {questionTypeselection === 'ratingscale' && (
                <Controller
                  name="ratingScale"
                  control={control}
                  render={({field}) => {
                    return <RatingScaleQuestionType field={field} />;
                  }}
                />
              )}
            </FormControl>
            {questionTypeselection != 'descriptive' && (
              <div>
                <RHFCheckbox
                  name="disableComments"
                  label="Disable Comment"
                  sx={{my: '5px'}}
                />
                <RHFCheckbox
                  name="makecommentmandatory"
                  label="Make Comment Mandatory"
                  sx={{my: '5px'}}
                />
              </div>
            )}
            <RHFCheckbox
              name="markasMandatory"
              label="Mark As Mandatory"
              sx={{my: '5px'}}
            />
            {questionTypeselection == 'star' && (
              <RHFCheckbox
                name="averagecourseRating"
                label="Use this question to calculate and display the average course rating"
                sx={{my: '5px'}}
              />
            )}
          </Container>
        </Scrollbar>

        <Box sx={{p: 2.5}}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={onSubmit}
            sx={{marginRight: '1vh'}}>
            Save
          </Button>
          <Button variant="soft" color="error" size="medium">
            Cancel
          </Button>
        </Box>
      </Drawer>
    </FormProvider>
  );
};

export default QuestionDialogFeedback;
